<template>
  <div>
    <v-divider id="divider"></v-divider>
    <div v-if="!errorGettingNotification">
      <v-form ref="form" v-model="valid">
        <v-container>
          <v-row>
            <v-col cols="12" class="d-flex justify-space-between">
              <v-spacer></v-spacer>
              <v-btn
                v-if="!editable && isAdmin"
                @click="toggleEditable()"
                color="secondary"
                text
              >
                <SvgIcon iconType="icon_edit_pen" />
                <span class="text-capitalize">{{
                  $t('fleet_management.agents.agent_detail.labels.button_edit')
                }}</span>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-form v-model="valid">
                <v-container>
                  <v-row>
                    <v-col cols="12" class="pb-0">
                      <h3>
                        {{
                          $t(
                            'fleet_management.agents.agent_detail.labels.notifications_recipients'
                          )
                        }}
                      </h3>
                    </v-col>
                    <v-col cols="auto">
                      <v-autocomplete
                        v-model="persons"
                        chips
                        dense
                        hide-details="auto"
                        :items="items"
                        item-text="fullName"
                        item-value="id"
                        :label="
                          $t(
                            'fleet_management.agents.agent_detail.labels.autocomplete_label'
                          )
                        "
                        multiple
                        return-object
                        :readonly="!editable"
                        :error-messages="errorMessage"
                      >
                        <template v-slot:selection="data">
                          <v-chip
                            v-bind="data.attrs"
                            :input-value="data.selected"
                            :close="editable"
                            @click="data.select"
                            @click:close="remove(data.item)"
                          >
                            <v-avatar left v-if="data.item.roles !== null">
                              <v-img
                                :src="data.item.avatar"
                                v-if="checkAvatar(data)"
                              ></v-img>
                              <v-icon>
                                mdi-account-circle
                              </v-icon>
                            </v-avatar>
                            {{ data.item.fullName }}
                          </v-chip>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" class="pb-0">
                      <h3>
                        {{
                          $t(
                            'fleet_management.agents.agent_detail.labels.notifications_title'
                          )
                        }}
                      </h3>
                    </v-col>
                    <v-col md="4">
                      <v-checkbox
                        class="mt-0"
                        @click="checkInputs"
                        color="secondary"
                        hide-details="auto"
                        :label="
                          $t(
                            'fleet_management.agents.agent_detail.labels.inbox_checkbox'
                          )
                        "
                        :readonly="!editable"
                        v-model="inbox"
                        :error-messages="errorCheckbox"
                      ></v-checkbox>
                    </v-col>
                    <v-col md="4">
                      <v-checkbox
                        class="mt-0"
                        v-model="byEmail"
                        :label="
                          $t(
                            'fleet_management.agents.agent_detail.labels.email_checkbox'
                          )
                        "
                        hide-details="auto"
                        color="secondary"
                        :readonly="!editable"
                        @click="checkInputs"
                        :error-messages="errorCheckbox"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-row v-if="editable" class="mb-5">
                <v-col align="end">
                  <div class="d-flex justify-space-between">
                    <v-btn depressed outlined rounded @click="cancel">{{
                      $t(
                        'fleet_management.agents.create.labels.create_agent_form_cancel_button'
                      )
                    }}</v-btn>
                    <v-btn
                      color="tertiary white--text"
                      depressed
                      rounded
                      :disabled="!valid || formHasNotChanged"
                      @click="confirmChanges"
                      :loading="submitLoading"
                      >{{
                        $t(
                          'fleet_management.agents.create.labels.create_agent_form_accept_button'
                        )
                      }}</v-btn
                    >
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </div>
    <div v-else>
      <v-container>
        <v-row>
          <v-col cols="12">
            <p class="h2 secondary--text font-weight-bold text-center">
              {{
                $t(
                  'fleet_management.agents.notifications.errors.unexpected_error'
                )
              }}
            </p>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <Dialog
      :showDialog="showDialog"
      :text="dialog.text"
      :closeButtonText="dialog.closeButtonText"
      :submitButtonText="dialog.acceptButtonText"
      :oneButton="dialog.oneButtonDialog"
      :oneButtonText="dialog.oneButtonText"
      :type="dialog.type"
      :loading="dialog.loading"
      @cancelButtonPressed="cancelDialog"
      @submitButtonPressed="aceptDialog"
      @oneButtonAction="cancelDialog"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PersonsHelpers from '@/helpers/persons/personsHelpers'
import NotificationsHelper from '@/helpers/notifications/notifications'
import { GlobalHelper } from '@/helpers/mainExports'
import AgentsHelpers from '@/helpers/agents/agentsHelpers'
import Dialog from '@/components/dialog/Dialog'
import { SvgIcon } from '@leapsight/styleguide'
import { i18n } from '@leapsight/portal'

export default {
  components: {
    SvgIcon,
    Dialog
  },
  props: {
    isAdmin: {
      type: Boolean
    },
    isEditable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      agent: [],
      byEmail: false,
      byEmailOriginal: false,
      dialog: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButtonText: '',
        oneButtonDialog: false
      },
      editable: false,
      errorGettingNotification: false,
      errorMessage: '',
      errorCheckbox: '',
      hasNotificationConfig: false,
      inbox: false,
      inboxOriginal: false,
      items: [],
      persons: [],
      personsId: [],
      personsNotificationConfig: [],
      personsNotificationIds: [], //utilizado como base para comparar si el formulario presento cambios
      showDialog: false,
      submitLoading: false,
      valid: true
    }
  },
  methods: {
    ...mapActions('ui', ['setSnackbar']),
    aceptDialog() {
      this.submit()
    },
    autoCheckInputs() {
      if (this.persons.length > 0) {
        for (let i = 0; i < this.persons.length; i++) {
          const person = this.persons[i]
          if (person.roles) {
            this.inbox = true
          } else {
            this.byEmail = true
          }
        }
      } else {
        this.inbox = false
        this.byEmail = false
      }
    },
    cancel() {
      this.fillNotifications()
      this.toggleEditable()
    },
    cancelDialog() {
      this.showDialog = false
      this.submitLoading = false
    },
    confirmChanges() {
      this.submitLoading = true
      this.showDialog = true
      this.dialog.type = 'warning'
      this.dialog.text = i18n.t(
        'fleet_management.agents.alerts.confirm_changes'
      )
    },
    checkAvatar(data) {
      return typeof data.item.avatar !== 'undefined' ? true : false
    },
    checkInputs() {
      let test = ''
      if (this.inbox) {
        test = ''
        let containContact = false
        let containUser = false
        for (let i = 0; i < this.persons.length; i++) {
          const person = this.persons[i]
          if (person.roles) {
            containUser = true
          } else {
            containContact = true
          }
        }
        if (containContact && containUser && !this.byEmail) {
          test = i18n.t(
            'fleet_management.agents.notifications.errors.email_checkbox_error'
          )
        } else {
          test = ''
        }

        if (containContact && !containUser) {
          test = i18n.t(
            'fleet_management.agents.notifications.errors.inbox_checkbox_error'
          )
        }
      }
      if (!this.inbox && !this.byEmail && this.persons.length > 0) {
        test = i18n.t(
          'fleet_management.agents.notifications.errors.empty_checkbox_error'
        )
      }
      if (this.persons.length == 0) {
        if (this.byEmail || this.inbox) {
          this.errorCheckbox = test = i18n.t(
            'fleet_management.agents.notifications.errors.checkbox_error'
          )
        } else {
          this.errorCheckbox = ''
        }
      } else {
        this.errorCheckbox = ''
      }

      this.errorMessage = test
    },

    fillNotifications() {
      this.resetValues()
      if (
        this.hasNotificationConfig &&
        this.personsNotificationConfig.length > 0
      ) {
        for (let i = 0; i < this.personsNotificationConfig.length; i++) {
          const element = this.personsNotificationConfig[i]
          if (element.channels) {
            if (element.channels.hasOwnProperty('email')) {
              this.byEmail = true
              this.byEmailOriginal = true
            }
            if (element.channels.hasOwnProperty('inbox')) {
              this.inbox = true
              this.inboxOriginal = true
            }
          }
        }
        for (let i = 0; i < this.items.length; i++) {
          const item = this.items[i]
          for (
            let index = 0;
            index < this.personsNotificationConfig.length;
            index++
          ) {
            const person = this.personsNotificationConfig[index]
            if (item.id === person.user_id) {
              this.persons.push(item)
            }
          }
        }
      }
    },

    async getAgent() {
      const agentId = this.$route.params.id
      const response = await AgentsHelpers.getAgent(agentId)
      this.agent = response.args[0]
    },
    getPersonsList() {
      PersonsHelpers.getPersonsList()
        .then(response => {
          const data = response.args[0]
          const newArr = data.map(item => {
            const myObj = {}
            const {
              account_id: accountId,
              emails,
              first_name: firstName,
              last_name: lastName,
              id,
              phones = null,
              roles = null
            } = item
            const fullName = `${item.first_name} ${item.last_name}`
            Object.assign(myObj, {
              accountId,
              emails,
              firstName,
              lastName,
              id,
              phones,
              roles,
              fullName
            })
            return myObj
          })
          newArr.sort((a, b) => {
            const result = a.firstName.localeCompare(b.firstName)

            return result !== 0 ? result : a.lastName.localeCompare(b.lastName)
          })
          this.items = newArr
          this.fillNotifications()
        })
        .catch(error => {
          if (error.error == 'com.magenta.error.operation_not_allowed') {
            const response = i18n.t(
              'fleet_management.persons.persons_tab_all.errors.no_permission'
            )
          } else {
            const response = i18n.t(
              'fleet_management.persons.persons_tab_all.errors.other_error'
            )
          }
        })
    },
    getNotification(_agentId) {
      NotificationsHelper.getNotification(_agentId).then(
        response => {
          this.hasNotificationConfig = true
          this.personsNotificationConfig = response.to
          this.personsNotificationIds = response.to.map(item => item.user_id)
          this.fillNotifications()
        },
        error => {
          if (error.error !== 'com.magenta.error.not_found') {
            this.errorGettingNotification = true
            const text = NotificationsHelper.getErrorMessageByCode(error.error)
            const snackbar = {
              text
            }
            this.setSnackbar(snackbar)
          }
        }
      )
    },
    remove(item) {
      const index = this.persons.indexOf(item)
      if (index >= 0) this.persons.splice(index, 1)
    },
    resetValues() {
      this.byEmail = false
      this.inbox = false
      this.byEmailOriginal = false
      this.inboxOriginal = false
      this.persons.splice(0) //reset value
    },

    submit() {
      this.submitLoading = true
      const agentId = this.$route.params.id
      const allPersons = []
      for (let i = 0; i < this.persons.length; i++) {
        const element = this.persons[i]
        const test = {}
        if (this.byEmail) {
          test = { ...test, email: element.emails[0].email }
        }
        if (this.inbox && element.roles !== null) {
          test = { ...test, inbox: element.id }
        }
        const person = {
          user_id: element.id,
          account_type: 'business',
          locale: 'es_AR',
          platform_user: element.roles !== null ? true : false,
          channels: { ...test }
        }
        allPersons.push(person)
      }
      //CREATE
      if (!this.hasNotificationConfig) {
        const conditionsAmount = Object.keys(this.agent.conditions).length
        const allCondition = []
        for (let i = 0; i < conditionsAmount; i++) {
          const element = Object.entries(this.agent.conditions)[i]
          switch (element[0]) {
            case 'mrn:agent_condition:speed_limit':
              const condition = {
                condition: element[0],
                type: 'undefined'
              }
              allCondition.push(condition)
              break
            case 'mrn:agent_condition:geofence': {
              const condition = {
                condition: element[0],
                type: element[1].type
              }
              allCondition.push(condition)
              break
            }
          }
        }
        const data = {
          agent_id: agentId,
          to: allPersons,
          enabled: true,
          timestamp: Math.floor(Date.now() / 1000),
          notifiable_events: allCondition
        }
        NotificationsHelper.createNotification(data).then(response => {
          //manejo de respuesta
          this.showDialog = false
          this.submitLoading = false
          // this.editable = false
          this.toggleEditable()
          if (!response.hasOwnProperty('error')) {
            const agentId = this.$route.params.id
            this.getNotification(agentId)
            this.getAgent()

            const snackbar = {
              color: 'success',
              text: i18n.t('fleet_management.agents.alerts.edit_success')
            }
            this.setSnackbar(snackbar)

            this.hasNotificationConfig = true
          } else {
            const snackbar = {
              text: i18n.t('fleet_management.agents.alerts.edit_error')
            }
            this.setSnackbar(snackbar)
          }
        })
      } else if (this.persons.length > 0) {
        //UPDATE
        const data = {
          id: agentId,
          data: {
            to: allPersons,
            timestamp: Math.floor(Date.now() / 1000)
          }
        }

        NotificationsHelper.updateNotification(data).then(response => {
          this.showDialog = false
          this.submitLoading = false
          if (!response.hasOwnProperty('error')) {
            const agentId = this.$route.params.id
            this.getNotification(agentId)
            this.getAgent()
            const snackbar = {
              color: 'success',
              text: i18n.t('fleet_management.agents.alerts.edit_success')
            }
            this.setSnackbar(snackbar)
          } else {
            const snackbar = {
              text: i18n.t('fleet_management.agents.alerts.edit_error')
            }
            this.setSnackbar(snackbar)
          }
          this.toggleEditable()
        })
      } else {
        //DELETE
        const notificationId = agentId

        NotificationsHelper.deleteNotification(notificationId).then(
          response => {
            this.showDialog = false
            this.submitLoading = false
            if (!response.hasOwnProperty('error')) {
              this.hasNotificationConfig = false
              this.resetValues()
              const snackbar = {
                color: 'success',
                text: i18n.t('fleet_management.agents.alerts.edit_success')
              }
              this.setSnackbar(snackbar)
            } else {
              const snackbar = {
                text: i18n.t('fleet_management.agents.alerts.edit_error')
              }
              this.setSnackbar(snackbar)
            }
            this.toggleEditable()
          }
        )
      }
    },
    toggleEditable() {
      this.editable = !this.editable
      this.$emit('notificationsEmit', this.editable)
    }
  },
  watch: {
    persons() {
      this.autoCheckInputs()
      this.checkInputs()
    }
  },
  computed: {
    ...mapGetters('auth', ['connection']),
    formHasNotChanged() {
      let value = true

      if (
        this.byEmail !== this.byEmailOriginal ||
        this.inbox !== this.inboxOriginal
      ) {
        value = false
      }

      //Agente sin notificaciones pasa a tener notificaciones
      if (!this.hasNotificationConfig && this.persons.length > 0) {
        value = false
      }
      //Agente con notificaciones se añaden o quitan personas
      if (this.hasNotificationConfig) {
        const newPersonsArrIds = this.persons.map(item => item.id)
        if (
          !GlobalHelper.haveSameContents(
            this.personsNotificationIds,
            newPersonsArrIds
          )
        )
          value = false
      }
      return value
    }
  },
  created() {
    const connection = this.connection
    if (!connection.session.isOpen) {
      setTimeout(() => {
        this.$nextTick(function() {
          this.getPersonsList()
          const agentId = this.$route.params.id
          this.getNotification(agentId)
          this.getAgent()
        })
      }, 1000)
    } else {
      this.getPersonsList()
      const agentId = this.$route.params.id
      this.getNotification(agentId)
      this.getAgent()
    }
    //if the prop "isEditable" is true sets de data ediatble on true to enable the editable mode
    this.editable = this.isEditable
  }
}
</script>

<style></style>
